import { onAuthStateChanged, type User } from 'firebase/auth';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import {
  type FacebookSignInResult,
  type GoogleCredentialTokens,
  type GoogleSignInResult,
  linkCurrentUserWithFacebookAccessToken,
  linkCurrentUserWithGoogleTokens,
  refreshCurrentUser as refreshCurrentUserService,
  requestCurrentUserEmailChange,
  sendPasswordReset as sendPasswordResetService,
  sendCurrentUserEmailVerification,
  setCurrentUserPassword,
  syncCurrentAuthEmail as syncCurrentAuthEmailService,
  consumeRegistrationMetaEventId,
  signInWithFacebookAccessToken,
  signInWithGoogleTokens,
  signInWithEmail,
  signOut as signOutService,
  signUpWithEmailAndFacebook,
  signUpWithEmailAndGoogle,
  signUpWithEmail,
} from '@/src/features/auth/auth-service';
import type { EmailVerificationFlow } from '@/src/features/auth/auth-action-routing';
import { auth } from '@/src/lib/firebase';
import { trackEvent } from '@/src/lib/analytics';
import {
  recordCrashlyticsError,
  setCrashlyticsUserId,
} from '@/src/lib/crashlytics';
import { recordSentryError } from '@/src/lib/sentry';

const AUTH_INIT_SLOW_MS = 3500;
const AUTH_INIT_TIMEOUT_MS = 12000;

type AuthContextValue = {
  user: User | null;
  initializing: boolean;
  authInitSlow: boolean;
  authInitTimedOut: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithFacebook: (accessToken: string) => Promise<FacebookSignInResult>;
  signInWithGoogle: (
    tokens?: GoogleCredentialTokens | null,
  ) => Promise<GoogleSignInResult>;
  signUp: (
    email: string,
    password: string,
    acceptedLegal: boolean,
    fullName: string,
    dateOfBirth: string,
    inviteCode?: string | null,
  ) => Promise<User>;
  signUpWithFacebook: (
    accessToken: string,
    email: string,
    password: string,
    acceptedLegal: boolean,
    fullName: string,
    dateOfBirth: string,
    inviteCode?: string | null,
  ) => Promise<User>;
  signUpWithGoogle: (
    tokens: GoogleCredentialTokens,
    email: string,
    password: string,
    acceptedLegal: boolean,
    fullName: string,
    dateOfBirth: string,
    inviteCode?: string | null,
  ) => Promise<User>;
  connectFacebook: (accessToken: string) => Promise<void>;
  connectGoogle: (tokens?: GoogleCredentialTokens | null) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  sendVerificationEmail: (flow?: EmailVerificationFlow) => Promise<void>;
  setPassword: (password: string, email?: string) => Promise<void>;
  refreshCurrentUser: () => Promise<User | null>;
  requestEmailChange: (
    newEmail: string,
    currentPassword: string,
    flow?: EmailVerificationFlow,
  ) => Promise<void>;
  syncCurrentAuthEmail: () => Promise<void>;
  signOut: () => Promise<void>;
  retryAuthInitialization: () => void;
};

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [authInitSlow, setAuthInitSlow] = useState(false);
  const [authInitTimedOut, setAuthInitTimedOut] = useState(false);
  const [authInitAttempt, setAuthInitAttempt] = useState(0);

  const applyAuthState = useCallback((nextUser: User | null) => {
    setCrashlyticsUserId(nextUser?.uid ?? null);
    setUser(nextUser);
    setInitializing(false);
    setAuthInitSlow(false);
    setAuthInitTimedOut(false);
  }, []);

  useEffect(() => {
    let settled = false;
    setInitializing(true);
    setAuthInitSlow(false);
    setAuthInitTimedOut(false);

    function finish(nextUser: User | null) {
      settled = true;
      applyAuthState(nextUser);
    }

    if (auth.currentUser) {
      finish(auth.currentUser);
    }

    const slowTimer = setTimeout(() => {
      if (settled) {
        return;
      }

      setAuthInitSlow(true);
      trackEvent('auth_init_slow');
    }, AUTH_INIT_SLOW_MS);

    const timeoutTimer = setTimeout(() => {
      if (settled) {
        return;
      }

      setInitializing(false);
      setAuthInitTimedOut(true);
      trackEvent('auth_init_timeout');
    }, AUTH_INIT_TIMEOUT_MS);

    const unsubscribe = onAuthStateChanged(
      auth,
      (nextUser) => {
        finish(nextUser);
      },
      (error) => {
        console.error('Failed to observe auth state.', error);
        recordCrashlyticsError(error, 'auth_state_observer_failed');
        recordSentryError(error, 'auth_state_observer_failed');
        finish(null);
      },
    );

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(timeoutTimer);
      unsubscribe();
    };
  }, [applyAuthState, authInitAttempt]);

  const retryAuthInitialization = useCallback(() => {
    setAuthInitAttempt((attempt) => attempt + 1);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const nextUser = await signInWithEmail({ email, password });
      applyAuthState(nextUser);
      trackEvent('login', { method: 'password' });
    },
    [applyAuthState],
  );

  const signInWithFacebook = useCallback(
    async (accessToken: string) => {
      const result = await signInWithFacebookAccessToken(accessToken);
      applyAuthState(result.user);
      trackEvent(result.isNewUser ? 'sign_up' : 'login', {
        method: 'facebook',
      });
      return result;
    },
    [applyAuthState],
  );

  const signInWithGoogle = useCallback(
    async (tokens?: GoogleCredentialTokens | null) => {
      const result = await signInWithGoogleTokens(tokens);
      applyAuthState(result.user);
      trackEvent(result.isNewUser ? 'sign_up' : 'login', {
        method: 'google',
      });
      return result;
    },
    [applyAuthState],
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      acceptedLegal: boolean,
      fullName: string,
      dateOfBirth: string,
      inviteCode?: string | null,
    ) => {
      const nextUser = await signUpWithEmail({
        email,
        password,
        acceptedLegal,
        fullName,
        dateOfBirth,
        inviteCode,
      });
      applyAuthState(nextUser);
      trackEvent('sign_up', {
        method: 'password',
        meta_event_id: consumeRegistrationMetaEventId(),
      });
      return nextUser;
    },
    [applyAuthState],
  );

  const signUpWithFacebook = useCallback(
    async (
      accessToken: string,
      email: string,
      password: string,
      acceptedLegal: boolean,
      fullName: string,
      dateOfBirth: string,
      inviteCode?: string | null,
    ) => {
      const nextUser = await signUpWithEmailAndFacebook({
        acceptedLegal,
        accessToken,
        dateOfBirth,
        email,
        fullName,
        inviteCode,
        password,
      });
      applyAuthState(nextUser);
      trackEvent('sign_up', {
        method: 'facebook',
        meta_event_id: consumeRegistrationMetaEventId(),
      });
      return nextUser;
    },
    [applyAuthState],
  );

  const signUpWithGoogle = useCallback(
    async (
      tokens: GoogleCredentialTokens,
      email: string,
      password: string,
      acceptedLegal: boolean,
      fullName: string,
      dateOfBirth: string,
      inviteCode?: string | null,
    ) => {
      const nextUser = await signUpWithEmailAndGoogle({
        acceptedLegal,
        dateOfBirth,
        email,
        fullName,
        inviteCode,
        password,
        tokens,
      });
      applyAuthState(nextUser);
      trackEvent('sign_up', {
        method: 'google',
        meta_event_id: consumeRegistrationMetaEventId(),
      });
      return nextUser;
    },
    [applyAuthState],
  );

  const signOut = useCallback(async () => {
    await signOutService();
    applyAuthState(null);
    trackEvent('logout');
  }, [applyAuthState]);

  const connectFacebook = useCallback(
    async (accessToken: string) => {
      const nextUser =
        await linkCurrentUserWithFacebookAccessToken(accessToken);
      applyAuthState(nextUser);
      trackEvent('auth_provider_link', { provider: 'facebook' });
    },
    [applyAuthState],
  );

  const connectGoogle = useCallback(
    async (tokens?: GoogleCredentialTokens | null) => {
      const nextUser = await linkCurrentUserWithGoogleTokens(tokens);
      applyAuthState(nextUser);
      trackEvent('auth_provider_link', { provider: 'google' });
    },
    [applyAuthState],
  );

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetService(email);
    trackEvent('password_reset_request');
  }, []);

  const sendVerificationEmail = useCallback(
    async (flow: EmailVerificationFlow = 'signup') => {
      await sendCurrentUserEmailVerification(flow);
      trackEvent('email_verification_request', {
        verification_flow: flow,
      });
    },
    [],
  );

  const refreshCurrentUser = useCallback(async () => {
    const nextUser = await refreshCurrentUserService();
    applyAuthState(nextUser);
    return nextUser;
  }, [applyAuthState]);

  const requestEmailChange = useCallback(
    async (
      newEmail: string,
      currentPassword: string,
      flow?: EmailVerificationFlow,
    ) => {
      await requestCurrentUserEmailChange({
        currentPassword,
        emailVerificationFlow: flow,
        newEmail,
      });
      trackEvent('email_change_verification_request');
    },
    [],
  );

  const syncCurrentAuthEmail = useCallback(async () => {
    await syncCurrentAuthEmailService();
    const nextUser = await refreshCurrentUserService();
    applyAuthState(nextUser);
    trackEvent('email_change_sync');
  }, [applyAuthState]);

  const setPassword = useCallback(
    async (password: string, email?: string) => {
      const nextUser = await setCurrentUserPassword(password, email);
      applyAuthState(nextUser);
      trackEvent('auth_password_link');
    },
    [applyAuthState],
  );

  const value = useMemo(
    () => ({
      user,
      initializing,
      authInitSlow,
      authInitTimedOut,
      connectFacebook,
      connectGoogle,
      retryAuthInitialization,
      refreshCurrentUser,
      requestEmailChange,
      sendPasswordReset,
      sendVerificationEmail,
      signIn,
      signInWithFacebook,
      signInWithGoogle,
      signUpWithFacebook,
      signUpWithGoogle,
      signUp,
      signOut,
      syncCurrentAuthEmail,
      setPassword,
    }),
    [
      authInitSlow,
      authInitTimedOut,
      connectFacebook,
      connectGoogle,
      initializing,
      retryAuthInitialization,
      refreshCurrentUser,
      requestEmailChange,
      sendPasswordReset,
      sendVerificationEmail,
      signIn,
      signInWithFacebook,
      signInWithGoogle,
      signUpWithFacebook,
      signUpWithGoogle,
      signOut,
      signUp,
      syncCurrentAuthEmail,
      setPassword,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
